import React from 'react';
import { useFlowAccountIntel } from '../contact/hooks';
import { safeString, fmtCurrency, severityColor, parseAccountIntelBlob } from './accountIntelBlob';

/**
 * Account-level Keystone intelligence panel — the "All" tab of the shared
 * `AccountIntelPanel` shell (plan
 * `plans/2026-09-23-002-rep-prompt-overrides-and-account-intelligence-summary-plan.md`
 * U8, R16). Renders the full Keystone treatment (Account Focus, Quick Stats,
 * Signals, Relationship, Deal) from the deep `state.state` blob, mirroring
 * action-view's TerraIntelligenceTab but built clean in core (no mini-app
 * import). The blob is unstructured and drifts across generator versions, so
 * every field is read defensively.
 *
 * Moved verbatim out of `AccountIntelPanel.tsx` (only the export name
 * changed) when that file became a Summary/All shell — the live-Keystone
 * fetch below now only fires once a rep clicks "All".
 *
 * The defensive parse itself (Account Focus / Quick Stats / Signals /
 * Relationship / Deal derivation) now lives in `accountIntelBlob.ts`'s
 * `parseAccountIntelBlob` (bead terra-design-v2-drilldowns-ppn.4, U1d) — this
 * component only renders the resulting `AccountIntelView`. `fmtDate` below
 * stays local: it's a presentational formatter for the two remaining raw
 * render sites (Last Contact, the Generated-at footer), not part of the
 * lifted derivation.
 */

function fmtDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

// ── Presentational primitives ────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-3 last:mb-0">
      <div className="text-[10px] font-bold uppercase tracking-[0.05em] text-content-tertiary dark:text-nightshade-500 mb-1">{title}</div>
      {children}
    </div>
  );
}

function KV({ label, value }: { label: string; value: unknown }) {
  const display = safeString(value);
  if (display == null) return null;
  return (
    <div className="flex gap-1.5 items-baseline mb-0.5">
      <span className="text-[10px] text-content-tertiary dark:text-nightshade-500 whitespace-nowrap">{label}</span>
      <span className="text-[12px] font-medium text-content-primary dark:text-nightshade-100">{display}</span>
    </div>
  );
}

const BADGE_COLORS: Record<'blue' | 'green' | 'amber' | 'red' | 'gray', string> = {
  blue: 'bg-bg-blue-light text-content-action dark:text-blueberry-400',
  green: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400',
  amber: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
  red: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
  gray: 'bg-bg-secondary dark:bg-nightshade-800 text-content-secondary dark:text-nightshade-400',
};
function Badge({ children, color = 'blue' }: { children: React.ReactNode; color?: keyof typeof BADGE_COLORS }) {
  return <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${BADGE_COLORS[color]}`}>{children}</span>;
}

function SignalGroup({ title, tone, items }: { title: string; tone: string; items: string[] }) {
  if (items.length === 0) return null;
  return (
    <div className="mb-1.5">
      <div className={`text-[10px] font-medium mb-0.5 ${tone}`}>{title}</div>
      {items.map(s => (
        <div key={s} className="text-[11px] text-content-secondary dark:text-nightshade-300 pl-2 leading-relaxed">• {s}</div>
      ))}
    </div>
  );
}

export function AccountIntelAllView({ accountId }: { accountId: string | null }) {
  const { data, isLoading, error } = useFlowAccountIntel(accountId);
  const muted = 'text-[12px] text-content-tertiary dark:text-nightshade-500';
  if (!accountId) return <div className={muted}>No account linked.</div>;
  if (isLoading) return <div className={muted}>Loading…</div>;
  if (error) return <div className={muted}>Intelligence unavailable.</div>;
  if (!data) return <div className={muted}>No intelligence for this account yet.</div>;

  const view = parseAccountIntelBlob(data);
  const { accountFocus, quickStats, signals, relationship, deal } = view;
  const { positive, negative, risks, opportunities } = signals;
  const { pipeline } = deal;

  const hasSignals = positive.length + negative.length + risks.length + opportunities.length > 0;
  const hasQuickStats = Boolean(quickStats.icpGrade || quickStats.buyingIntent || quickStats.relationshipHealth)
    || quickStats.employees != null;

  if (!view.hasContent) return <div className={muted}>No intelligence data available.</div>;

  return (
    <div>
      {accountFocus && (accountFocus.type || accountFocus.strategy) && (
        <Section title="Account Focus">
          {accountFocus.type && <div className="mb-1"><Badge color="blue">{accountFocus.type}</Badge></div>}
          {accountFocus.strategy && (
            <p className="text-[12px] leading-relaxed text-content-secondary dark:text-nightshade-300">{accountFocus.strategy}</p>
          )}
        </Section>
      )}

      {hasQuickStats && (
        <Section title="Quick Stats">
          <div className="grid grid-cols-2 gap-x-4">
            <KV label="ICP Grade" value={quickStats.icpGrade} />
            <KV label="Buying Intent" value={quickStats.buyingIntent} />
            <KV label="Rel. Health" value={quickStats.relationshipHealth} />
            <KV label="Employees" value={quickStats.employees} />
            <KV label="Segment" value={quickStats.segment} />
            <KV label="Industry" value={quickStats.industry} />
            <KV label="Champion" value={quickStats.champion} />
            <KV label="Econ. Buyer" value={quickStats.econBuyer} />
          </div>
          {quickStats.icpReasoning && (
            <p className="text-[11px] text-content-tertiary dark:text-nightshade-400 mt-1 leading-relaxed">{quickStats.icpReasoning}</p>
          )}
        </Section>
      )}

      {hasSignals && (
        <Section title="Signals">
          <SignalGroup title="Positive" tone="text-emerald-600 dark:text-emerald-400" items={positive} />
          <SignalGroup title="Negative" tone="text-red-600 dark:text-red-400" items={negative} />
          {risks.length > 0 && (
            <div className="mb-1.5">
              <div className="text-[10px] font-medium text-amber-600 dark:text-amber-400 mb-0.5">Risks</div>
              {risks.map(r => (
                <div key={`${r.severity ?? ''}:${r.risk ?? ''}`} className="text-[11px] text-content-secondary dark:text-nightshade-300 pl-2 mb-0.5 leading-relaxed">
                  <Badge color={severityColor(r.severity)}>{r.severity ?? 'Risk'}</Badge>{' '}
                  {r.risk}
                  {r.mitigation && <span className="text-content-tertiary dark:text-nightshade-500"> — {r.mitigation}</span>}
                </div>
              ))}
            </div>
          )}
          {opportunities.length > 0 && (
            <div>
              <div className="text-[10px] font-medium text-content-action dark:text-blueberry-400 mb-0.5">Opportunities</div>
              {opportunities.map(o => (
                <div key={`${o.priority ?? ''}:${o.opportunity ?? ''}`} className="text-[11px] text-content-secondary dark:text-nightshade-300 pl-2 mb-0.5 leading-relaxed">
                  {o.priority && <Badge color={o.priority.toLowerCase() === 'high' ? 'amber' : 'gray'}>{o.priority}</Badge>}{' '}
                  {o.opportunity}
                </div>
              ))}
            </div>
          )}
        </Section>
      )}

      {relationship && (relationship.health || relationship.momentum || relationship.touchpointFrequency) && (
        <Section title="Relationship">
          <div className="grid grid-cols-2 gap-x-4">
            <KV label="Health" value={relationship.health} />
            <KV label="Momentum" value={relationship.momentum} />
            <KV label="Touchpoints" value={relationship.touchpointFrequency} />
            <KV label="Last Contact" value={fmtDate(relationship.lastContactDate)} />
          </div>
          {Array.isArray(relationship.keyRelationships) && relationship.keyRelationships.length > 0 && (
            <div className="mt-1">
              {relationship.keyRelationships.map(r => {
                const label = safeString(r);
                return <div key={label ?? JSON.stringify(r)} className="text-[11px] text-content-secondary dark:text-nightshade-300">{label}</div>;
              })}
            </div>
          )}
        </Section>
      )}

      {deal && (deal.primaryDeal || pipeline.length > 0 || deal.history) && (
        <Section title="Deal">
          {deal.primaryDeal && (
            <div className="mb-1.5">
              <div className="text-[12px] font-semibold text-content-primary dark:text-nightshade-100">{deal.primaryDeal.name ?? 'Primary Deal'}</div>
              <div className="grid grid-cols-2 gap-x-4 mt-0.5">
                <KV label="Stage" value={deal.primaryDeal.stage} />
                <KV label="Amount" value={fmtCurrency(deal.primaryDeal.amount)} />
                <KV label="Close Date" value={fmtDate(deal.primaryDeal.closeDate)} />
              </div>
            </div>
          )}
          {pipeline.length > 0 && (
            <div>
              <div className="text-[10px] font-medium text-content-tertiary dark:text-nightshade-500 mb-0.5">Pipeline ({pipeline.length})</div>
              {pipeline.map(p => (
                <div key={`${p.name ?? ''}:${p.stage ?? ''}`} className="text-[11px] text-content-secondary dark:text-nightshade-300">
                  {p.name} — {p.stage}{p.amount ? ` (${fmtCurrency(p.amount)})` : ''}
                </div>
              ))}
            </div>
          )}
          {deal.history && <p className="text-[11px] text-content-tertiary dark:text-nightshade-400 mt-1 leading-relaxed">{deal.history}</p>}
        </Section>
      )}

      {view.fitScoreSummary && (
        <Section title="Fit">
          <p className="text-[12px] leading-relaxed text-content-secondary dark:text-nightshade-300">{view.fitScoreSummary}</p>
        </Section>
      )}
      {view.propensityScoreReasoning && (
        <Section title="Propensity">
          <p className="text-[12px] leading-relaxed text-content-secondary dark:text-nightshade-300">{view.propensityScoreReasoning}</p>
        </Section>
      )}

      {view.generatedAt && (
        <div className="mt-2 text-[10.5px] text-content-tertiary dark:text-nightshade-500">Generated {fmtDate(view.generatedAt)}</div>
      )}
    </div>
  );
}
